import { DECISION_CONSOLE_READ_MODEL_SCHEMA_VERSION } from '../decision-console';
import {
  MA_SYNERGY_COMMITMENT_SCHEMA_VERSION,
  MA_SYNERGY_UNDERWRITING_CONTEXT_KIND,
} from '../ma-synergy-commitment';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// K3 (2026-08-18): ma-synergy-tracking evidence-contract registration (board row
// masyn-1; the board names it by its key namespace `ma_fusion_synergy`). The
// module's only required key, `ma_fusion_synergy_underwriting`
// (ma-synergy-tracking.manifest.ts:8, MA_FUSION_SYNERGY_EVIDENCE_KEY), had no
// registry entry.
//
// origin literals: IMPORTED from packages/lib's own ma-synergy-commitment.ts
// (MA_SYNERGY_UNDERWRITING_CONTEXT_KIND / MA_SYNERGY_COMMITMENT_SCHEMA_VERSION) —
// the same constants the engine's source queries
// (ma-synergy-tracking.source.ts:65-66,92-93). This is the #4449
// imported-literal discipline in its literal form: no re-typed strings anywhere.
//
// SOURCE CLASS / FRESHNESS: the manifest declares sourceClass 'modeled' with
// maxAgeHours 720 (30 days), staleBehavior block. No cadence bucket equals 30
// days; `weekly` is registered so the contract can never report the packet
// fresher than the module's own declaration (the segment-margin precedent).
// Disclosed divergence: the module's runtime treats age as advisory since the
// age-never-blocks change (ma-synergy-tracking.source.ts:14-19 — "an approved
// underwriting packet does not become untrue on day 31"), so the stricter
// cadence only makes readiness reporting conservative; it withholds nothing the
// module would serve.
export const MA_FUSION_SYNERGY_EVIDENCE_KEYS = {
  underwriting: 'ma_fusion_synergy_underwriting',
} as const;

// SECOND EVIDENCE AUTHORITY (#4764 amendment, Codex review 5337133168).
//
// The module has always read from two authorities, and its per-metric evidence
// mapping already says so: `underwritten_c2_modelled_upside_usd` resolves from the
// approved underwriting packet above, while `realized_observed_actuals_usd`,
// `committed_watched_c1_usd` and `at_risk_projected_miss_usd` resolve from the
// governed Decision Console realization read model. Any formula whose inputs cross
// both authorities must DECLARE both — a contract naming only the underwriting
// packet would misstate where its realized numerator and committed denominator
// came from.
//
// This authority is NOT an ingest-anchored artifact: the Decision Console read model
// is derived at request time from governed commitments and observed actuals, so it
// carries no evidence-pack row to register as an artifact entry. It is therefore
// declared as a named key + schema version here (the ONE place either string is
// written), consumed by the module manifest's `requiredState` /
// `proofPolicy.requiredEvidenceRefs` and by the scoreboard evidence-ref builder.
export const MA_SYNERGY_SCOREBOARD_EVIDENCE_KEY = 'ma_synergy_scoreboard' as const;

export const MA_SYNERGY_SCOREBOARD_EVIDENCE_SCHEMA_VERSION =
  DECISION_CONSOLE_READ_MODEL_SCHEMA_VERSION;

export const MA_FUSION_SYNERGY_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'ma-synergy-tracking',
  {
    ma_fusion_synergy_underwriting: defineModuleEvidenceEntry(
      MA_FUSION_SYNERGY_EVIDENCE_KEYS.underwriting,
      {
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
        contextKind: MA_SYNERGY_UNDERWRITING_CONTEXT_KIND,
        schemaVersion: MA_SYNERGY_COMMITMENT_SCHEMA_VERSION,
        bindingPlaceholders: [],
        domain: 'strategy',
        freshnessCadence: 'weekly',
        requiredFor: ['monitoring', 'decision_pack', 'ask_helix'],
        missingBehavior: 'block',
        description:
          'Approved M&A synergy underwriting packet; the governed synergy ' +
          'commitment record every synergy tracking, projected-miss, and ' +
          'realization read resolves from.',
      },
    ),
  },
  {
    evidenceKeys: Object.values(MA_FUSION_SYNERGY_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.modeled],
    contextKinds: [MA_SYNERGY_UNDERWRITING_CONTEXT_KIND],
    schemaVersions: [MA_SYNERGY_COMMITMENT_SCHEMA_VERSION],
    stateKeys: Object.values(MA_FUSION_SYNERGY_EVIDENCE_KEYS),
    bindingPlaceholders: [],
  },
  [
    // #4764 amendment 14: the manifest's evidence-COMPLETENESS declarations
    // (governedCapability.requiredEvidence and the deal-canvas intent) now carry the
    // second authority, so the K3 census requires its typed classification here. It is
    // request-time derived — no AskHelixMemoryArtifact substrate — exactly as the
    // SECOND EVIDENCE AUTHORITY note above records.
    {
      evidenceKey: MA_SYNERGY_SCOREBOARD_EVIDENCE_KEY,
      sourceAuthority:
        'DecisionConsoleReadModelService.getDecisionConsole — the governed Decision ' +
        'Console realization read model (decision_console_read_model_v1), read at ' +
        'request time by ma-synergy-tracking.module-definition.ts',
      reason:
        'Derived at request time from governed commitments and observed actuals; it ' +
        'carries no ingest-anchored artifact row to register as an artifact entry.',
    },
  ],
);

export type MaFusionSynergyModuleEvidenceContract =
  typeof MA_FUSION_SYNERGY_MODULE_EVIDENCE_CONTRACT;
